<script setup>
import { ref, computed, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useCmsUiVisible } from '@/composables/useCmsUiVisible.js'
import { isReviewLive } from '@/cms/reviewGate.js'
import {
  SITE_URL,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_TYPE,
  DEFAULT_OG_IMAGE_ALT,
  withTrailingSlash
} from '@/utils/siteUrl'
import ContentHero from '@/components/content/ContentHero.vue'
import ContentBlocks from '@/components/content/ContentBlocks.vue'

const isCmsEnabled = import.meta.env.MODE === 'cms'
const CmsEditorModal = isCmsEnabled
  ? defineAsyncComponent(() => import('@/cms/components/CmsEditorModal.vue'))
  : null

const { cmsUiVisible } = useCmsUiVisible()
const showCmsChrome = computed(() => isCmsEnabled && cmsUiVisible.value)

const route = useRoute()

const modules = import.meta.glob('/src/content/pages/**/*.md', { eager: true })
const slug    = computed(() => route.params.slug)
const section = computed(() => route.meta.section)

const isEditing   = ref(false)
const contentPath = computed(() => `/src/content/pages/${section.value}/${slug.value}.md`)

const frontmatter = computed(() => {
  const mod = modules[contentPath.value]
  if (!mod) return null
  const fm = mod.frontmatter || mod   // unplugin-vue-markdown exposes both
  return {
    title:       fm.title,
    excerpt:     fm.excerpt || '',
    image:       fm.image || '',
    publishedAt: fm.publishedAt || null,
    reviewStatus: fm.reviewStatus || '',
    hero:        fm.hero || {},
    blocks:      fm.blocks || [],
    density:     fm.density || '',   // 'compact' tightens the block rhythm
    noindex:     !!fm.noindex,       // true → robots noindex,nofollow (unlisted pages, e.g. launch comms)
  }
})

const breadcrumbs = computed(() => {
  if (!frontmatter.value) return []
  return [
    { label: 'Home', to: '/' },
    { label: route.meta.backLabel, to: route.meta.backTo },
    { label: frontmatter.value.title },
  ]
})

const isPublished = computed(() => {
  if (!frontmatter.value) return true
  if (import.meta.env.DEV) return true
  const p = frontmatter.value.publishedAt
  return (!p || new Date(p) <= new Date()) && isReviewLive(frontmatter.value.reviewStatus)
})

// Full SEO head — mirrors usePageHead.js's tag set (og/twitter/canonical) but
// stays inside a computed so the head updates on in-app nav between content
// pages. Public URL + canonical are derived from the route, not contentPath.
const baseUrl = import.meta.env.VITE_APP_BASE_URL || SITE_URL

useHead(computed(() => {
  const fm = frontmatter.value
  const title = fm ? fm.title : 'Not found'
  const description = fm?.excerpt || ''
  const url = `${baseUrl}${withTrailingSlash(`/${section.value}/${slug.value}`)}`
  // Sitewide default share image (jk-35) unless this page sets its own.
  const hasCustomImage = Boolean(fm?.hero?.image || fm?.image)
  const ogImage = fm?.hero?.image || fm?.image || DEFAULT_OG_IMAGE
  const ogImageAbs = `${baseUrl}${ogImage}`
  return {
    title,
    meta: [
      { name: 'description', content: description },
      ...(fm?.noindex ? [{ name: 'robots', content: 'noindex, nofollow' }] : []),
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: url },
      { property: 'og:image', content: ogImageAbs },
      { name: 'twitter:image', content: ogImageAbs },
      // Known dimensions only apply to the default image — a page's own
      // image may be a different size, so we don't claim to know it.
      ...(hasCustomImage ? [] : [
        { property: 'og:image:width', content: DEFAULT_OG_IMAGE_WIDTH },
        { property: 'og:image:height', content: DEFAULT_OG_IMAGE_HEIGHT },
        { property: 'og:image:type', content: DEFAULT_OG_IMAGE_TYPE },
        { property: 'og:image:alt', content: DEFAULT_OG_IMAGE_ALT }
      ]),
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ],
    link: [{ rel: 'canonical', href: url }],
  }
}))
</script>

<template>
  <div class="content-page" :class="`page-${slug}`">
    <template v-if="!frontmatter">
      <div class="container state">
        <h1>Not found</h1>
        <RouterLink :to="withTrailingSlash(route.meta.backTo)">← Back to {{ route.meta.backLabel }}</RouterLink>
      </div>
    </template>

    <template v-else-if="!isPublished">
      <div class="container state">
        <p class="not-published">This page is not yet available.</p>
        <div class="back"><RouterLink :to="withTrailingSlash(route.meta.backTo)">← Back to {{ route.meta.backLabel }}</RouterLink></div>
      </div>
    </template>

    <template v-else>
      <div v-if="showCmsChrome" class="container cms-bar">
        <button class="edit-inline" @click="isEditing = true">Edit</button>
      </div>
      <CmsEditorModal v-if="isCmsEnabled && isEditing" :content-path="contentPath" @close="isEditing = false" />

      <ContentHero
        :title="frontmatter.title"
        :hero="frontmatter.hero"
        :breadcrumbs="breadcrumbs"
        :colour="frontmatter.hero?.colour"
      />

      <div class="blocks" :class="{ compact: frontmatter.density === 'compact' }">
        <ContentBlocks :blocks="frontmatter.blocks" />
      </div>

      <div class="container back"><RouterLink :to="withTrailingSlash(route.meta.backTo)">← Back to {{ route.meta.backLabel }}</RouterLink></div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.content-page {
  padding-bottom: $space-8;

  .state { max-width: $content-prose; padding-block: $space-8; }

  .cms-bar {
    display: flex;
    justify-content: flex-end;
    padding-top: $space-4;
  }

  .edit-inline {
    background: none;
    border: 1px solid var(--color-border);
    border-radius: $radius-sm;
    color: var(--color-text-muted);
    cursor: pointer;
    font-family: $font-sans;
    font-size: $font-size-cap;
    font-weight: 600;
    padding: 2px $space-2;
    @include transition(color, border-color, background);

    &:hover         { color: var(--color-primary); border-color: var(--color-primary); }
    &:focus-visible { @include focus-ring; }
    &:active        { transform: translateY(1px); }
  }

  // Top-level block stack — the single content container (blocks lay out
  // within this capped column); generous vertical rhythm between sections.
  .blocks {
    max-width: $container;
    margin-inline: auto;
    padding-inline: $gutter;
    display: flex;
    flex-direction: column;
    gap: $space-8;
    @include from-md { gap: $space-10; }

    // Compact density (frontmatter `density: compact`) — for pages built from
    // many small blocks (short paragraphs, lists, quotes), where the default
    // 64/128px rhythm leaves far too much air. Used by the Academy program pages.
    &.compact {
      gap: $space-6;
      @include from-md { gap: $space-12; }
    }
  }

  // Vision-mission-values only: align the Vision/Mission `section` rows to the
  // Values `iconList` row — match its 32px column gap so their content columns
  // share a left edge (flush with the value icons), and its 20px body text.
  // iconList is used only on this page; ContentSection/SplitHeaderBlock are
  // shared, so these overrides live here rather than changing them globally.
  &.page-vision-mission-and-values {
    :deep(.content-section .inner) {
      @include from-lg { gap: $space-6; }   // 64px → 32px, matches SplitHeaderBlock
    }
    :deep(.content-section .prose) {
      font-size: $font-size-lg;             // 16px → 20px, matches the values text
    }
  }

  .back {
    margin-top: $space-8;
    font-size: $font-size-sm;

    a { color: var(--color-primary); text-decoration: none; &:hover { text-decoration: underline; } }
  }

  .not-published {
    padding: $space-10 0;
    color: var(--color-text-muted);
    font-style: italic;
  }
}
</style>
